import React, { memo, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import party from 'party-js';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation } from 'swiper';

import { Avatar, Spinner } from '@chakra-ui/react';
import { H2, Paragraph } from '../../components/Typography';
import LockSVG from '../../components/Icons/Lock';
import { useMomentsContext } from './MomentsContext';
import { ANIMATION_CLASSNAME, MomentApplication } from './moments.types';
import PreviousSVG from './assets/PreviousArrow';
import NextSVG from './assets/NextArrow';

export const MomentsSlider = memo((): JSX.Element => {
  const { moments, setSwiperRef, isLoading } = useMomentsContext();
  const [prevEl, setPrevEl] = useState<HTMLElement | null>(null);
  const [nextEl, setNextEl] = useState<HTMLElement | null>(null);

  if (isLoading) {
    return (
      <SpinnerWrapper>
        <Spinner size="lg" />
      </SpinnerWrapper>
    );
  }

  const filteredMoments = moments.filter(
    (momentApp) => momentApp.moment.isVisible,
  );

  if (filteredMoments.length === 0) {
    return null;
  }

  return (
    <MomentsSliderWrapper>
      <SwiperCustom
        onSwiper={setSwiperRef}
        spaceBetween={50}
        slidesPerView="auto"
        autoHeight
        speed={500}
        tag="section"
        navigation={{ prevEl, nextEl }}
        modules={[Navigation]}
        breakpoints={{
          416: {
            slidesPerView: 2,
          },
          768: {
            slidesPerView: 3,
          },
          1024: {
            slidesPerView: 5,
          },
        }}
      >
        {filteredMoments.map((momentApplication) => (
          <SwiperSlide id={`moment-slide-${momentApplication?.moment?.id}`} key={momentApplication.id} className="awt-moment-slide">
            <MomentCard momentApplication={momentApplication} />
          </SwiperSlide>
        ))}
      </SwiperCustom>
      <div className="arrow-button arrow-previous" ref={setPrevEl}>
        <PreviousSVG />
      </div>
      <div className="arrow-button arrow-next" ref={setNextEl}>
        <NextSVG />
      </div>
    </MomentsSliderWrapper>
  );
});

const MomentCard = memo(
  ({ momentApplication }: { momentApplication: MomentApplication }) => {
    const ref = useRef<HTMLDivElement>(null);
    const isEarned = momentApplication.isEarned;
    const isClaimed = momentApplication.isClaimed;
    const isReernable = momentApplication.moment.isReearnable;
    const { onAnimationEnd, className } = useMomentsContext();
    const moment = momentApplication.moment;

    useEffect(() => {
      if (className === `${ANIMATION_CLASSNAME}-${moment.id}`) {
        const classNames = ref.current?.className.split(' ');

        if (!classNames.find((name) => name === 'awt-confetti-off')) {
          setTimeout(() => {
            party.confetti(ref.current);
          }, 500);
        }
      }
    }, [className, moment.id]);

    const cardClasses = [
      ...(className ? [className] : []),
      ...(isEarned ? ['awt-moment-earned'] : []),
      ...(isClaimed ? ['awt-moment-claimed'] : []),
      ...(isReernable ? ['awt-moment-reearnable'] : []),
    ];

    return (
      <MomentCardContainer
        ref={ref}
        id={moment.id}
        onAnimationEnd={onAnimationEnd}
        className={cardClasses.join(' ')}
      >
        <Avatar
          className="awt-moment-avatar"
          size="xl"
          src={isEarned ? moment.badgeOnSrc : moment.badgeOffSrc}
          icon={<LockSVG />}
          showBorder={false}
          borderColor="transparent"
          backgroundColor="transparent"
        />
        <MomentSlideTitle modifiers="body">
          {moment.displayName}
        </MomentSlideTitle>
        <MomentSlideDescription modifiers="subdued">
          {moment.description}
        </MomentSlideDescription>
      </MomentCardContainer>
    );
  },
);

const SwiperCustom = styled(Swiper).attrs({
  className: 'awt-moments-slider',
})``;

const MomentCardContainer = styled.article.attrs<
  { className: string } & React.HTMLAttributes<HTMLDivElement>
>(({ className }) => ({
  className: className ? `awt-moment-card ${className}` : 'awt-moment-card',
}))<{ id: string; className: string } & React.HTMLAttributes<HTMLDivElement>>`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  gap: 0.8rem;

  &.${({ id }) => `${ANIMATION_CLASSNAME}-${id}`} {
    animation-name: heartBeat;
    animation-duration: 1.5s;
    animation-timing-function: ease-in-out;
    animation-delay: 0.5s;
  }

  @keyframes heartBeat {
    0% {
      transform: scale(1);
    }

    14% {
      transform: scale(1.3);
    }

    28% {
      transform: scale(1);
    }

    42% {
      transform: scale(1.3);
    }

    70% {
      transform: scale(1);
    }
  }
`;

const MomentsSliderWrapper = styled.div`
  position: relative;
  width: 100%;
  margin-bottom: 20px;
  font-size: 16px;
  isolation: isolate;

  .swiper-button-lock {
    display: none;
  }
  .swiper-button-disabled {
    opacity: 0.5;
  }

  .arrow-button {
    position: absolute;
    top: 20%;
    cursor: pointer;
    z-index: 1;
  }
  .arrow-previous {
    left: -30px;
  }
  .arrow-next {
    right: -30px;
  }

  @media (max-width: 768px) {
    .arrow-previous {
      left: -10px;
    }

    .arrow-next {
      right: -10px;
    }
  }
`;

const MomentSlideTitle = styled(H2).attrs({
  className: 'awt-moment-slide-title',
})`
  font-size: 0.9em;
  color: var(--chakra-colors-black);
  text-align: center;
`;

const MomentSlideDescription = styled(Paragraph).attrs({
  className: 'awt-moment-slide-description',
})`
  font-size: 0.75em;
  color: var(--chakra-colors-gray-400);
  text-align: center;
`;

const SpinnerWrapper = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
  min-height: 150px;
  margin-bottom: 20px;
`;
